import { formatPersonName } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNotificationPreferences } from '@/features/notifications/use-notifications';
import { usePageTitle } from '@/hooks/use-page-title';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { useProfileEdit } from '@/platform/features/profile/profile-edit-context';
import { ProfilePhotoCard } from '@/platform/features/profile/profile-photo-card';
import { ProfilePhotoPreview } from '@/platform/features/profile/profile-photo-preview';
import { ProfilePublicFields } from '@/platform/features/profile/profile-public-card';
import {
  BirthdayFields,
  ProfilePageShell,
  ProfileSettingsSection,
  roleLabelOf,
} from '@/platform/features/profile/profile-shared';
import type { PushPermissionState } from '@/platform/features/notifications/push/push-support';
import { usePushSubscription } from '@/platform/features/notifications/push/use-push-subscription';
import { useVoice, useVoiceCopy } from '@/platform/voice/use-voice';
import {
  PROFILE_DEVICES_PATH,
  PROFILE_NOTIFICATIONS_SETTINGS_PATH,
} from '@/platform/lib/profile-paths';

export function ProfileOverviewPage() {
  usePageTitle('Profil');
  const voice = useVoice();
  const copy = useVoiceCopy();
  const { profile, phone, setPhone, overviewDirty, savingOverview, saveOverviewSettings } =
    useProfileEdit();
  const preferences = useNotificationPreferences(true);
  const push = usePushSubscription(true);
  const roleLabel = roleLabelOf(profile.roles);

  const pushLine = pushStatusSentence(
    push.supported,
    push.serverEnabled,
    push.permission,
    push.isActiveOnThisDevice,
  );
  const emailLine =
    preferences.data?.orgEmailEnabled === false
      ? copy('platform.profile.email.off')
      : copy('platform.profile.email.on');

  return (
    <ProfilePageShell>
      <PlatformPageHeader title="Profil" description={copy('platform.profile.account')} />

      <div className="flex items-center gap-4">
        <ProfilePhotoPreview
          profile={profile}
          className="size-14 shrink-0"
          avatarClassName="text-lg"
        />
        <div className="min-w-0">
          <p className="truncate text-lg font-semibold">{formatPersonName(profile, voice)}</p>
          <p className="text-sm text-muted-foreground">
            {[roleLabel, profile.departmentName].filter(Boolean).join(' · ') || 'Mitarbeiter'}
          </p>
        </div>
      </div>

      <form
        className="overflow-hidden rounded-xl border border-border bg-card shadow-sm"
        onSubmit={(event) => void saveOverviewSettings(event)}
      >
        <ProfileSettingsSection
          title="Profilfoto"
          description="JPEG, PNG oder WebP, höchstens 2 MB. Sichtbar für angemeldete Kolleginnen und Kollegen."
        >
          <ProfilePhotoCard profile={profile} embedded />
        </ProfileSettingsSection>

        <div className="border-t border-border">
          <ProfileSettingsSection
            title="Erreichbarkeit"
            description="Telefon ist optional und wird mit den übrigen Angaben gespeichert."
          >
            <div>
              <Label htmlFor="phone">Telefon</Label>
              <Input
                id="phone"
                className="mt-2"
                value={phone}
                autoComplete="tel"
                onChange={(event) => setPhone(event.target.value)}
              />
            </div>
          </ProfileSettingsSection>
        </div>

        <div className="border-t border-border">
          <ProfileSettingsSection
            title="Für Kolleginnen und Kollegen"
            description="Stellenbezeichnung, Abteilung, Standort und ein kurzer Text."
          >
            <ProfilePublicFields />
          </ProfileSettingsSection>
        </div>

        <div className="border-t border-border">
          <ProfileSettingsSection
            title="Geburtstag und Jubiläum"
            description={copy('platform.profile.birthday')}
          >
            <BirthdayFields />
          </ProfileSettingsSection>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-border bg-muted/30 px-4 py-4 sm:px-6">
          <Button type="submit" disabled={!overviewDirty || savingOverview}>
            {savingOverview ? <Loader2Icon className="animate-spin" aria-hidden /> : null}
            Speichern
          </Button>
        </div>
      </form>

      <ul className="hidden space-y-2 text-sm sm:block">
        <li>
          <span className="text-muted-foreground">Push auf diesem Gerät: </span>
          {push.isLoading ? (
            <Loader2Icon className="inline size-4 animate-spin" aria-hidden />
          ) : (
            <span className="font-medium">{pushLine}</span>
          )}
          {' · '}
          <Link
            to={PROFILE_DEVICES_PATH}
            className="text-primary underline-offset-4 hover:underline"
          >
            Geräte
          </Link>
        </li>
        <li>
          <span className="text-muted-foreground">E-Mail-Benachrichtigungen: </span>
          {preferences.isPending ? (
            <Loader2Icon className="inline size-4 animate-spin" aria-hidden />
          ) : (
            <span className="font-medium">{emailLine}</span>
          )}
          {' · '}
          <Link
            to={PROFILE_NOTIFICATIONS_SETTINGS_PATH}
            className="text-primary underline-offset-4 hover:underline"
          >
            Mitteilungen
          </Link>
        </li>
      </ul>
    </ProfilePageShell>
  );
}

function pushStatusSentence(
  supported: boolean,
  serverEnabled: boolean,
  permission: PushPermissionState,
  isActiveOnThisDevice: boolean,
): string {
  if (!supported) {
    return 'Nicht unterstützt';
  }
  if (!serverEnabled) {
    return 'Derzeit nicht verfügbar';
  }
  if (permission === 'denied') {
    return 'Blockiert';
  }
  if (isActiveOnThisDevice) {
    return 'Aktiv auf diesem Gerät';
  }
  if (permission === 'granted') {
    return 'Berechtigung erteilt, noch nicht registriert';
  }
  return 'Nicht aktiv';
}
